/**
 * FFmpeg's part in the archive, as arguments (nothing is run here): what a file is (ffprobe's JSON, read), its preview
 * copy and its picture, a part of it for a partial restore, and how far ffmpeg has got. The archive agent at the
 * station and the AirPost server (a preview made from a file sent from the item's card) run the very same commands,
 * so a preview is the same wherever it was made; a browser that makes one itself makes it to the same measure
 * (`PREVIEW`).
 *
 * The preview copy: 640×360 inside black bars, deinterlaced, H.264 at a low rate with a keyframe every second, AAC
 * stereo, fast start; picture and sound only (no timecode track, none of the master's metadata). A part for a
 * restore: an HD file's part is made again in XDCAM HD 422 (MPEG-2 4:2:2 at 50 Mb/s, 24-bit PCM, MXF: what the
 * station's recorders write and its playout plays), from the frame at IN to the frame before OUT, its fields in their
 * order; any other file's part is copied out as it is, from the keyframe at or before IN.
 */
/** The preview copy and its picture, wherever they are made. */
export const PREVIEW = { width: 640, height: 360, videoBps: 700_000, audioBps: 96_000, thumbWidth: 480, thumbHeight: 270 };
const rate = (s) => {
    const m = s ? /^(\d+)\/(\d+)$/.exec(s) : null;
    if (!m || Number(m[2]) === 0)
        return null;
    const v = Number(m[1]) / Number(m[2]);
    return v > 0 && v < 240 ? Math.round(v * 1000) / 1000 : null;
};
/** ffprobe's JSON (`-print_format json -show_format -show_streams`) as the archive keeps it. */
export function parseProbe(json) {
    const p = JSON.parse(json);
    const v = p.streams?.find((s) => s.codec_type === 'video');
    const a = p.streams?.find((s) => s.codec_type === 'audio');
    const container = p.format?.format_name?.split(',')[0] ?? null;
    const fo = v?.field_order;
    return {
        container,
        videoCodec: v?.codec_name ?? null,
        width: v?.width ?? null,
        height: v?.height ?? null,
        fps: rate(v?.r_frame_rate) ?? rate(v?.avg_frame_rate),
        scan: !v ? null : fo && fo !== 'progressive' && fo !== 'unknown' ? 'interlaced' : 'progressive',
        audioCodec: a?.codec_name ?? null,
        audioChannels: a?.channels ?? null,
        durationMs: Math.max(0, Math.round(Number(p.format?.duration ?? 0) * 1000)),
        bitrate: p.format?.bit_rate ? Math.round(Number(p.format.bit_rate)) : null,
    };
}
/** The ffprobe arguments `parseProbe` reads. */
export const probeArgs = (file) => ['-v', 'error', '-print_format', 'json', '-show_format', '-show_streams', file];
/** The preview copy's frame rate: a 50/60 Hz source at half (the preview needs no more), at least something sensible. */
export const proxyFps = (fps) => (!fps ? 25 : fps > 30.5 ? fps / 2 : fps);
/** The preview copy (see above); with `progress`, ffmpeg reports how far it is on stdout. */
export function proxyArgs(src, out, info, opts = {}) {
    const fps = proxyFps(info.fps);
    const vf = [info.scan === 'interlaced' ? 'yadif=mode=0:deint=all' : null, `fps=${fps.toFixed(3)}`, `scale=${PREVIEW.width}:${PREVIEW.height}:force_original_aspect_ratio=decrease`, `pad=${PREVIEW.width}:${PREVIEW.height}:(ow-iw)/2:(oh-ih)/2:black`, 'setsar=1', 'format=yuv420p']
        .filter(Boolean)
        .join(',');
    const gop = String(Math.max(12, Math.round(fps)));
    return ['-v', 'error', '-nostdin', '-y', ...(opts.progress ? ['-progress', 'pipe:1', '-nostats'] : []), '-i', src, '-map', '0:v:0', '-map', '0:a:0?', '-vf', vf, '-c:v', 'libx264', '-preset', 'veryfast', '-crf', '30', '-maxrate', '800k', '-bufsize', '1600k', '-g', gop, '-keyint_min', gop, '-sc_threshold', '0', '-c:a', 'aac', '-b:a', '96k', '-ac', '2', '-ar', '48000', '-movflags', '+faststart', '-write_tmcd', '0', '-map_metadata', '-1', '-f', 'mp4', out];
}
/** Where the item's picture is taken: a quarter in, at most 10 s. */
export const thumbAtMs = (durationMs) => Math.min(durationMs * 0.25, 10_000);
/** The item's picture: 480×270 JPEG. */
export function thumbArgs(src, out, durationMs) {
    const at = thumbAtMs(durationMs) / 1000;
    return ['-v', 'error', '-nostdin', '-y', ...(at > 0 ? ['-ss', at.toFixed(3)] : []), '-i', src, '-map', '0:v:0', '-frames:v', '1', '-vf', `scale=${PREVIEW.thumbWidth}:${PREVIEW.thumbHeight}:force_original_aspect_ratio=decrease,pad=${PREVIEW.thumbWidth}:${PREVIEW.thumbHeight}:(ow-iw)/2:(oh-ih)/2:black,setsar=1`, '-q:v', '4', '-f', 'image2', out];
}
/** Whether a part of this file is made in XDCAM HD 422: an HD picture (1080 or 720 lines) the format carries. */
export const partAsXdcam = (info) => !!info.videoCodec && ((info.width === 1920 && info.height === 1080) || (info.width === 1280 && info.height === 720));
/**
 * A part of the file, from IN for OUT − IN, progress on stdout. In XDCAM HD 422 when `partAsXdcam` (frame-accurate,
 * every sound kept as 24-bit PCM, interlaced pictures kept top field first); otherwise every stream copied as it is.
 */
export function extractArgs(src, out, inMs, outMs, info) {
    const head = ['-v', 'error', '-nostdin', '-y', '-progress', 'pipe:1', '-nostats', '-ss', (inMs / 1000).toFixed(3), '-i', src, '-t', ((outMs - inMs) / 1000).toFixed(3)];
    if (!info || !partAsXdcam(info))
        return [...head, '-map', '0', '-c', 'copy', out];
    const interlaced = info.scan === 'interlaced';
    return [
        ...head,
        '-map', '0:v:0', '-map', '0:a?',
        '-vf', [interlaced ? 'setfield=tff' : null, 'format=yuv422p'].filter(Boolean).join(','),
        '-c:v', 'mpeg2video', '-b:v', '50M', '-minrate', '50M', '-maxrate', '50M', '-bufsize', '17825792', '-rc_init_occupancy', '17825792',
        '-g', '12', '-bf', '2', '-qmin', '1', '-qmax', '12', '-intra_vlc', '1', '-non_linear_quant', '1', '-sc_threshold', '1000000000',
        ...(interlaced ? ['-flags', '+ildct+ilme'] : []),
        '-color_primaries', 'bt709', '-color_trc', 'bt709', '-colorspace', 'bt709',
        '-c:a', 'pcm_s24le', '-ar', '48000',
        '-f', 'mxf', out,
    ];
}
/** The time ffmpeg has written so far, from its -progress lines (out_time_us, or out_time_ms which is also µs). */
export function progressMs(chunk) {
    let last = null;
    for (const m of chunk.matchAll(/out_time_(?:us|ms)=(\d+)/g))
        last = Math.round(Number(m[1]) / 1000);
    return last;
}
//# sourceMappingURL=archive-ffmpeg.js.map